import { useState, useEffect, useCallback } from 'react';
import {
  fetchSupplierLocations,
  createLocation,
  updateLocation,
  deleteLocation,
} from '../api/supplierLocationsApi';
import type {
  SupplierLocationView,
  CreateSupplierLocationInput,
  UpdateSupplierLocationInput,
} from '../types';

interface UseSupplierLocationsOptions {
  supplierId: string | null;
  autoFetch?: boolean;
}

interface UseSupplierLocationsReturn {
  locations: SupplierLocationView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addLocation: (input: Omit<CreateSupplierLocationInput, 'supplier_id'>) => Promise<void>;
  updateLocationById: (locationId: string, input: UpdateSupplierLocationInput) => Promise<void>;
  removeLocation: (locationId: string) => Promise<void>;
  isAdding: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

/**
 * Hook to fetch and manage supplier locations
 */
export function useSupplierLocations({
  supplierId,
  autoFetch = true,
}: UseSupplierLocationsOptions): UseSupplierLocationsReturn {
  const [locations, setLocations] = useState<SupplierLocationView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLocations = useCallback(async () => {
    if (!supplierId) {
      setLocations([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchSupplierLocations(supplierId);
      setLocations(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load locations';
      setError(message);
      console.error('[useSupplierLocations] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [supplierId]);

  const addLocation = useCallback(
    async (input: Omit<CreateSupplierLocationInput, 'supplier_id'>) => {
      if (!supplierId) return;

      setIsAdding(true);
      setError(null);

      try {
        await createLocation({
          ...input,
          supplier_id: supplierId,
        });
        // Refetch to get updated view data
        await loadLocations();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to add location';
        setError(message);
        console.error('[useSupplierLocations] Add error:', err);
        throw err;
      } finally {
        setIsAdding(false);
      }
    },
    [supplierId, loadLocations]
  );

  const updateLocationById = useCallback(
    async (locationId: string, input: UpdateSupplierLocationInput) => {
      setIsUpdating(true);
      setError(null);

      try {
        await updateLocation(locationId, input);
        // Refetch to get updated view data
        await loadLocations();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update location';
        setError(message);
        console.error('[useSupplierLocations] Update error:', err);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [loadLocations]
  );

  const removeLocation = useCallback(
    async (locationId: string) => {
      setIsDeleting(true);
      setError(null);

      try {
        await deleteLocation(locationId);
        // Remove from local state immediately
        setLocations((prev) => prev.filter((loc) => loc.id !== locationId));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete location';
        setError(message);
        console.error('[useSupplierLocations] Delete error:', err);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    []
  );

  useEffect(() => {
    if (autoFetch && supplierId) {
      loadLocations();
    }
  }, [autoFetch, supplierId, loadLocations]);

  return {
    locations,
    isLoading,
    error,
    refetch: loadLocations,
    addLocation,
    updateLocationById,
    removeLocation,
    isAdding,
    isUpdating,
    isDeleting,
  };
}
